import { ComponentProps, JSXElementConstructor } from 'react';
import {
  registerBottomSheets,
  BottomSheetComponent,
  NavigationConfig,
  registerNavigationHandlers,
  registerModals,
  ParamListBase,
} from '@bonanza-social/navigation';
import {
  StatusUpdated,
  StatusProgressInfo,
  StatusBoxes,
  LegacyStatusInfo,
} from '../bottomSheets';
import {
  StatusDemotionModal,
  StatusPromotionModal,
  StatusRetentionModal,
  StatusOnboardingModal,
} from '../modals';
import { openStatusModal } from '../utils/status';
import StatusDemotionScreen from '../screens/StatusDemotionScreen';
import StatusPromotionScreen from '../screens/StatusPromotionScreen';
import StatusRetentionScreen from '../screens/StatusRetentionScreen';
import StatusChangeHeader from '../components/StatusChange/StatusChangeHeader/StatusChangeHeader';
import StatusOnboardingScreen from '../screens/StatusOnboardingScreen';
import StatusInfoScreen from '../screens/StatusInfoScreen';
import StatusTransmit from '../bottomSheets/StatusTransmit/StatusTransmit';

type StatusParamsList = Pick<
  AppNavigatorParamsList,
  | 'StatusDemotion'
  | 'StatusPromotion'
  | 'StatusRetention'
  | 'StatusOnboarding'
  | 'StatusInfo'
>;

export const NAVIGATION_CONFIG: NavigationConfig<StatusParamsList> = {
  FULL_SCREENS: [
    {
      name: 'StatusInfo',
      options: {},
      component: StatusInfoScreen,
    },
  ],
  FULL_SCREEN_MODALS: [],
  FULL_SCREEN_MODALS_TRANSPARENT: [
    {
      name: 'StatusDemotion',
      options: {
        header: (props) => (
          <StatusChangeHeader headerShown={props.options.headerShown} />
        ),
      },
      component: StatusDemotionScreen,
    },
    {
      name: 'StatusPromotion',
      options: ({ route }) => ({
        header: (props) => (
          <StatusChangeHeader headerShown={props.options.headerShown} />
        ),
        ...route.params,
      }),
      component: StatusPromotionScreen,
    },
    {
      name: 'StatusRetention',
      options: {
        header: (props) => (
          <StatusChangeHeader headerShown={props.options.headerShown} />
        ),
      },
      component: StatusRetentionScreen,
    },
    {
      name: 'StatusOnboarding',
      options: {
        header: (props) => (
          <StatusChangeHeader headerShown={props.options.headerShown} />
        ),
        headerShown: false,
        animation: 'fade',
      },
      component: StatusOnboardingScreen,
    },
  ],
};

const bottomSheets = {
  statusUpdated: StatusUpdated,
  statusProgressInfo: StatusProgressInfo,
  legacyStatusInfo: LegacyStatusInfo,
  statusBoxes: StatusBoxes,
  statusTransmit: StatusTransmit,
};

const modals = {
  StatusDemotion: StatusDemotionModal,
  StatusPromotion: StatusPromotionModal,
  StatusRetention: StatusRetentionModal,
  StatusOnboarding: StatusOnboardingModal,
};

registerBottomSheets('status', bottomSheets);

registerModals('status', modals);

registerNavigationHandlers<
  AppNavigatorParamsList,
  | 'StatusInfo'
  | 'StatusDemotion'
  | 'StatusPromotion'
  | 'StatusRetention'
  | 'StatusOnboarding'
>({
  StatusInfo(navigate) {
    navigate('/status');
  },
  StatusDemotion() {
    openStatusModal('StatusDemotion');
  },
  StatusPromotion() {
    openStatusModal('StatusPromotion');
  },
  StatusRetention() {
    openStatusModal('StatusRetention');
  },
  StatusOnboarding() {
    openStatusModal('StatusOnboarding');
  },
});

declare global {
  interface AppNavigatorParamsList extends ParamListBase {
    StatusDemotion: undefined;
    StatusPromotion?: { animation?: 'default' | 'fade' };
    StatusRetention: undefined;
    StatusOnboarding: undefined;
    StatusInfo: undefined;
  }
}

declare global {
  interface RegisteredBottomSheets {
    status: Record<keyof typeof bottomSheets, BottomSheetComponent>;
  }

  interface RegisteredModals {
    status: {
      [K in keyof typeof modals]: JSXElementConstructor<
        ComponentProps<(typeof modals)[K]>
      >;
    };
  }
}
